import React, { useState, useEffect } from 'react';
import { Search, TrendingUp, Hash, Users, Grid3X3 } from 'lucide-react';
import { DiscoverGrid } from '../components/explore/DiscoverGrid';
import { TrendingHashtags } from '../components/hashtag/TrendingHashtags';
import { HashtagChip } from '../components/hashtag/HashtagChip';
import { useAuthStore } from '../store/authStore';
import { getPopularPosts, getTrendingHashtags, getRecommendedUsers, searchByHashtag } from '../lib/discoveryService';
import { posts } from '../data/mockData';
import { Post, User } from '../types';

export const ExplorePage: React.FC = () => {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'popular' | 'trending' | 'users'>('popular');
  const [popularPosts, setPopularPosts] = useState<Post[]>([]);
  const [trendingHashtags, setTrendingHashtags] = useState<{ hashtag: string; count: number }[]>([]);
  const [recommendedUsers, setRecommendedUsers] = useState<User[]>([]);
  const [selectedHashtag, setSelectedHashtag] = useState<string | null>(null);
  const [hashtagPosts, setHashtagPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  const categories = [
    { name: 'Popular', hashtag: null },
    { name: 'Dogs', hashtag: 'dogs' },
    { name: 'Cats', hashtag: 'cats' },
    { name: 'Birds', hashtag: 'birds' },
    { name: 'Reptiles', hashtag: 'reptiles' },
    { name: 'Fish', hashtag: 'fish' },
    { name: 'Small Pets', hashtag: 'smallpets' },
    { name: 'Outdoors', hashtag: 'outdoors' },
    { name: 'Funny', hashtag: 'funny' }
  ];

  useEffect(() => {
    loadExploreData();
  }, [user]);

  useEffect(() => {
    if (selectedHashtag) {
      loadHashtagPosts(selectedHashtag);
    }
  }, [selectedHashtag]);

  const loadExploreData = async () => {
    try {
      setLoading(true);
      
      const [popular, trending, recommended] = await Promise.all([
        getPopularPosts(30),
        getTrendingHashtags(15),
        getRecommendedUsers(user?.id || '', 10)
      ]);

      // Combine with mock posts for demo
      const allPopularPosts = [...popular, ...posts].filter((post, index, self) => 
        index === self.findIndex(p => p.id === post.id)
      );

      setPopularPosts(allPopularPosts);
      setTrendingHashtags(trending);
      setRecommendedUsers(recommended);
    } catch (error) {
      console.error('Error loading explore data:', error);
      // Fallback to mock data
      setPopularPosts(posts);
    } finally {
      setLoading(false);
    }
  };

  const loadHashtagPosts = async (hashtag: string) => {
    try {
      const posts = await searchByHashtag(hashtag, 20);
      setHashtagPosts(posts);
    } catch (error) {
      console.error('Error loading hashtag posts:', error);
      setHashtagPosts([]);
    }
  };

  const handleCategoryClick = (category: { name: string; hashtag: string | null }) => {
    if (category.hashtag) {
      setSelectedHashtag(category.hashtag);
      setActiveTab('popular');
    } else {
      setSelectedHashtag(null);
      setHashtagPosts([]);
    }
  };

  const handleBackToPopular = () => {
    setSelectedHashtag(null);
    setHashtagPosts([]);
  };

  if (loading) {
    return (
      <div className="pt-16 pb-16 md:pt-20 md:pb-0 bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 max-w-screen-lg">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 pb-16 md:pt-20 md:pb-0 bg-gray-50">
      <div className="container mx-auto px-4 max-w-screen-lg">
        {/* Header */}
        <div className="bg-white border border-gray-200 rounded-md p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-gray-900">Explore</h1>
            {selectedHashtag && (
              <button
                onClick={handleBackToPopular}
                className="text-sm text-purple-600 hover:text-purple-700 transition-colors"
              >
                ← Back to explore
              </button>
            )}
          </div>

          {/* Current Selection */}
          {selectedHashtag && (
            <div className="mb-4 p-3 bg-purple-50 rounded-lg">
              <div className="flex items-center justify-between">
                <HashtagChip hashtag={selectedHashtag} variant="purple" />
                <span className="text-sm text-purple-600">
                  {hashtagPosts.length} posts found
                </span>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('popular')}
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors flex items-center justify-center ${
                activeTab === 'popular'
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Grid3X3 className="h-4 w-4 mr-2" />
              {selectedHashtag ? `#${selectedHashtag}` : 'Popular'}
            </button>
            <button
              onClick={() => setActiveTab('trending')}
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors flex items-center justify-center ${
                activeTab === 'trending'
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              Trending
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors flex items-center justify-center ${
                activeTab === 'users'
                  ? 'bg-white text-purple-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Users className="h-4 w-4 mr-2" />
              Users
            </button>
          </div>
        </div>

        {/* Categories */}
        {activeTab === 'popular' && !selectedHashtag && (
          <div className="mb-6 overflow-x-auto">
            <div className="flex space-x-2 py-2">
              {categories.map((category, index) => (
                <button 
                  key={index}
                  onClick={() => handleCategoryClick(category)}
                  className="bg-white text-gray-800 px-4 py-2 rounded-full text-sm font-medium border border-gray-200 hover:bg-purple-50 hover:border-purple-200 hover:text-purple-700 transition-colors whitespace-nowrap"
                >
                  {category.hashtag && <Hash className="h-3 w-3 mr-1 inline" />}
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        {activeTab === 'popular' && (
          <DiscoverGrid posts={selectedHashtag ? hashtagPosts : popularPosts} />
        )}

        {activeTab === 'trending' && (
          <TrendingHashtags limit={20} showTitle={false} variant="card" />
        )}

        {activeTab === 'users' && (
          <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <Users className="h-5 w-5 mr-2" />
                Suggested for You
              </h2>
              <p className="text-sm text-gray-500 mt-1">Discover new pet accounts to follow</p>
            </div>
            <div className="divide-y divide-gray-200">
              {recommendedUsers.length > 0 ? (
                recommendedUsers.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-4">
                    <div className="flex items-center">
                      <img
                        src={user.profileImage}
                        alt={user.username}
                        className="h-12 w-12 rounded-full object-cover mr-4"
                      />
                      <div>
                        <p className="font-medium text-gray-900">{user.username}</p>
                        <p className="text-sm text-gray-500">{user.displayName}</p>
                        {user.bio && (
                          <p className="text-xs text-gray-400 mt-1 max-w-xs truncate">{user.bio}</p>
                        )}
                      </div>
                    </div>
                    <button className="bg-purple-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-purple-700 transition-colors">
                      Follow
                    </button>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium mb-2">No suggestions available</p>
                  <p className="text-sm">Check back later for new user recommendations</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};